// =============================================================
// User-facing HTML templates — login, change-password, profile,
// agenda calendar. All copy comes from the i18n dictionaries;
// this module is pure HTML composition.
// =============================================================

import { t, getLocale } from './i18n';

const esc = (s: string) => s
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

// ---------- AUTH (login + change-password) ----------
export const loginTpl = (): string => `
  <section class="auth">
    <div class="auth__card">
      <p class="auth__brand">${esc(t('login.brand'))}</p>
      <h1 class="auth__title">${esc(t('login.title'))}</h1>
      <form id="loginForm" class="auth__form" novalidate>
        <div class="field">
          <label for="loginEmail">${esc(t('login.email'))}</label>
          <input id="loginEmail" type="email" required autocomplete="username" />
        </div>
        <div class="field">
          <label for="loginPassword">${esc(t('login.password'))}</label>
          <input id="loginPassword" type="password" required autocomplete="current-password" />
        </div>
        <button class="btn btn--primary btn--block" type="submit">
          <span>${esc(t('login.submit'))}</span>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="square" stroke-linejoin="miter" /></svg>
        </button>
        <p class="auth__error" id="loginError" role="alert" hidden></p>
      </form>
      <p class="auth__hint">${esc(t('login.signUp'))}</p>
    </div>
  </section>
`;

export const changePasswordTpl = (): string => `
  <section class="auth">
    <div class="auth__card">
      <p class="auth__brand">${esc(t('login.brand'))}</p>
      <h1 class="auth__title">${esc(t('changePassword.title'))}</h1>
      <p class="auth__intro">${esc(t('changePassword.intro'))}</p>
      <form id="changePasswordForm" class="auth__form" novalidate>
        <div class="field">
          <label for="cpCurrent">${esc(t('changePassword.current'))} <span class="field__opt">${esc(t('changePassword.currentOptional'))}</span></label>
          <input id="cpCurrent" type="password" autocomplete="current-password" />
        </div>
        <div class="field">
          <label for="cpNew">${esc(t('changePassword.new'))}</label>
          <input id="cpNew" type="password" minlength="8" required autocomplete="new-password" />
          <small class="field__hint">${esc(t('changePassword.newHelp'))}</small>
        </div>
        <div class="field">
          <label for="cpConfirm">${esc(t('changePassword.confirm'))}</label>
          <input id="cpConfirm" type="password" minlength="8" required autocomplete="new-password" />
        </div>
        <button class="btn btn--primary btn--block" type="submit">
          <span>${esc(t('changePassword.submit'))}</span>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="square" stroke-linejoin="miter" /></svg>
        </button>
        <p class="auth__error" id="cpError" role="alert" hidden></p>
      </form>
    </div>
  </section>
`;

// ---------- PROFILE ----------
export const profileTpl = (firstName: string, lastName: string, email: string, phone: string): string => `
  <section class="profile">
    <header class="section-head reveal">
      <p class="section-eyebrow">${esc(t('profile.title'))}</p>
      <h1 class="section-title">${esc(firstName)} ${esc(lastName)}</h1>
      <p class="section-sub">${esc(t('profile.intro'))}</p>
    </header>
    <div class="profile__grid reveal">
      <article class="profile__card">
        <h2 class="profile__card-title">${esc(t('profile.sectionAccount'))}</h2>
        <form id="profileForm" class="profile__form" novalidate>
          <div class="field-row">
            <div class="field">
              <label for="pfFirst">${esc(t('profile.firstName'))}</label>
              <input id="pfFirst" type="text" value="${esc(firstName)}" required />
            </div>
            <div class="field">
              <label for="pfLast">${esc(t('profile.lastName'))}</label>
              <input id="pfLast" type="text" value="${esc(lastName)}" required />
            </div>
          </div>
          <div class="field">
            <label for="pfEmail">${esc(t('profile.email'))}</label>
            <input id="pfEmail" type="email" value="${esc(email)}" required autocomplete="email" />
          </div>
          <div class="field">
            <label for="pfPhone">${esc(t('profile.phone'))}</label>
            <input id="pfPhone" type="tel" value="${esc(phone)}" autocomplete="tel" />
          </div>
          <button class="btn btn--primary" type="submit">
            <span>${esc(t('profile.save'))}</span>
          </button>
          <p class="form-status" id="profileStatus" role="status" aria-live="polite"></p>
        </form>
      </article>
      <article class="profile__card">
        <h2 class="profile__card-title">${esc(t('profile.sectionCredentials'))}</h2>
        <p class="profile__card-help">${esc(t('changePassword.intro'))}</p>
        <a class="btn btn--ghost" href="#change-password" id="goChangePassword">
          <span>${esc(t('profile.changePassword'))}</span>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="square" stroke-linejoin="miter" /></svg>
        </a>
      </article>
      <article class="profile__card">
        <h2 class="profile__card-title">${esc(t('profile.sectionBooking'))}</h2>
        <p class="profile__card-help">${esc(t('profile.bookSlotHelp'))}</p>
        <a class="btn btn--primary" href="/${getLocale()}/agenda" data-nav data-slug="agenda" id="goAgenda">
          <span>${esc(t('profile.bookSlot'))}</span>
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="square" stroke-linejoin="miter" /></svg>
        </a>
      </article>
      <article class="profile__card">
        <h2 class="profile__card-title" data-i18n="profile.sectionSession">${esc(t('profile.sectionSession'))}</h2>
        <p class="profile__card-help" data-i18n="profile.logoutHelp">${esc(t('profile.logoutHelp'))}</p>
        <button class="btn btn--danger" type="button" id="profileLogout">
          <span data-i18n="profile.logout">${esc(t('profile.logout'))}</span>
        </button>
      </article>
    </div>
  </section>
`;

// ---------- AGENDA APP ----------
// Renders a layout shell. The actual data + interactivity is wired
// in src/lib/agendaApp.ts, which mounts into #agendaGrid.
//
// All translatable strings use `data-i18n` so `applyI18n()` on
// locale change updates them in place (no full route re-render).
export const agendaAppTpl = (): string => `
  <section class="agenda-app" aria-labelledby="agendaAppTitle">
    <header class="agenda-app__head reveal">
      <p class="section-eyebrow" data-i18n="agendaApp.title">${esc(t('agendaApp.title'))}</p>
      <h1 class="section-title" id="agendaAppTitle" data-i18n="agendaApp.title">
        <span>${esc(t('agendaApp.title'))}</span>
      </h1>
      <p class="section-sub" data-i18n="agendaApp.subtitle">${esc(t('agendaApp.subtitle'))}</p>
      <p class="agenda-app__hint" data-i18n="agendaApp.slotHelp">${esc(t('agendaApp.slotHelp'))}</p>
    </header>

    <div class="agenda-app__controls reveal">
      <button class="btn btn--ghost btn--small" type="button" id="weekPrev" aria-label="${esc(t('agendaApp.weekPrev'))}">
        <span aria-hidden="true">←</span>
        <span data-i18n="agendaApp.weekPrev">${esc(t('agendaApp.weekPrev'))}</span>
      </button>
      <button class="btn btn--ghost btn--small" type="button" id="weekToday" data-i18n="agendaApp.weekToday">${esc(t('agendaApp.weekToday'))}</button>
      <button class="btn btn--ghost btn--small" type="button" id="weekNext" aria-label="${esc(t('agendaApp.weekNext'))}">
        <span data-i18n="agendaApp.weekNext">${esc(t('agendaApp.weekNext'))}</span>
        <span aria-hidden="true">→</span>
      </button>
      <span class="agenda-app__range" id="agendaRange" aria-live="polite"></span>
      <div class="agenda-app__location reveal" role="tablist" aria-label="Studio">
        <button class="agenda-app__pill is-active" role="tab" data-loc="A22" aria-selected="true">A22</button>
        <button class="agenda-app__pill" role="tab" data-loc="M15" aria-selected="false">M15</button>
      </div>
    </div>

    <div class="agenda-app__legend reveal" aria-label="Legend">
      <span class="legend legend--open" data-i18n="agendaApp.legend.open">${esc(t('agendaApp.legend.open'))}</span>
      <span class="legend legend--event" data-i18n="agendaApp.legend.event">${esc(t('agendaApp.legend.event'))}</span>
      <span class="legend legend--closed" data-i18n="agendaApp.legend.closed">${esc(t('agendaApp.legend.closed'))}</span>
      <span class="legend legend--taken" data-i18n="agendaApp.legend.taken">${esc(t('agendaApp.legend.taken'))}</span>
      <span class="legend legend--pending" data-i18n="agendaApp.legend.pending">${esc(t('agendaApp.legend.pending'))}</span>
      <span class="legend legend--mine" data-i18n="agendaApp.legend.mine">${esc(t('agendaApp.legend.mine'))}</span>
    </div>

    <div class="agenda-app__layout reveal">
      <div class="agenda-app__grid" id="agendaGrid" aria-busy="true">
        <p class="agenda__empty" data-i18n="agendaApp.loading">${esc(t('agendaApp.loading'))}</p>
      </div>
    </div>
  </section>
`;
